import React from 'react';
import ProductCard, { ProductCardProps } from '@components/organisms/catalog/components/product-card/product-card';
import { useContextSelector } from 'use-context-selector';
import { UserContext } from '@context/user.context';
import { CatalogContext } from '@context/catalog.context';
import { usePreferences } from '@context/preferences.context';
import { useAnalytics } from '@context/analytics.context';
import getCatalogQueryIndex from '@helpers/utils/catalog-query-index';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import AuthGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import styles from '@components/catalog/product-search/product-search.module.scss';
import { useAuthentication } from '@context/authentication.context';
import useWindowSize from '@hooks/use-window-size';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { useSessionStorage } from 'usehooks-ts';
import { useBucket } from '@context/bucket.context';
import { getCatalogTrackingData, getProductTrackingData } from '@helpers/utils/catalog/catalog-tracking-utils';
import useCatalogSchema from '@hooks/catalog-product/use-catalog-schema';
import { CatalogScrollPosition, ScrollPositionType } from '@hooks/catalog-context/use-restore-catalog-scroll-position';
import { useRouter } from 'next/router';

const CatalogPageProductCard: React.FC<Omit<ProductCardProps, 'product'> & { product: CatalogProduct }> = (
  props,
): React.JSX.Element => {
  const { product, index } = props;

  const searchQuery = useContextSelector(CatalogContext, (v) => v.searchQuery);
  const sortBy = useContextSelector(CatalogContext, (v) => v.sortBy);
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);
  const resultsPerPage = useContextSelector(CatalogContext, (v) => v.resultsPerPage);
  const currentPage = useContextSelector(CatalogContext, (v) => v.currentPage);
  const schemaCatalogPage = useCatalogSchema();
  const userSizes = useContextSelector(UserContext, (v) => v.userSizes);
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const { isWiderThanMd } = useWindowSize();
  const [, setPlpScrollPosition] = useSessionStorage<CatalogScrollPosition>('plpScrollPositionConfig', null);
  const { shouldDisplayAuthDialog, setShouldDisplayAuthDialog } = useAuthentication();
  const { setRedirectUrl } = useAuthentication();
  const { country } = usePreferences();
  const { sendEvent } = useAnalytics();
  const { isFeatureEnabled } = useBucket();
  const isGuestXpPdp = isFeatureEnabled((f) => f.guestXpPdp, true);
  const isShowTaxIconEnabled = isFeatureEnabled((f) => f.enableWebDutiesPlpXp, true);
  const isGoodPriceVariantEnabled = isFeatureEnabled((f) => f.enableGoodPriceProductCard, true);
  const { query } = useRouter();
  const alertId = query?.alertId;

  const clickOnProduct = () => {
    const areSizesEnabled = areMySizesEnabled;
    const position = ((currentPage - 1) * resultsPerPage + index).toString();
    setPlpScrollPosition({ type: ScrollPositionType.ProductTile, productId: product.id });

    // Old event, to be removed
    sendEvent({
      type: 'event',
      payload: {
        event: 'clickResults',
        index_name: getCatalogQueryIndex(country, sortBy),
        keyword: searchQuery || 'no keyword',
        perso_created: !!userSizes,
        ...(!!userSizes && { perso_enabled: areSizesEnabled }),
        perso_product_click: undefined, // TODO should be determined based on whether the product is from the personalized list or not
        position,
        product_id: product?.id,
      },
    });

    // new snowplow evemt
    sendEvent({
      type: 'event',
      payload: {
        event: 'product_click',
        ...getCatalogTrackingData(),
        ...getProductTrackingData(product),
        product_position: String(isWiderThanMd ? (currentPage - 1) * resultsPerPage + index : index),
        product_feed_category: alertId ? `saved_searches_${alertId}` : 'catalog',
      },
    });
  };

  return (
    <ConditionalWrapper
      condition={!isGuestXpPdp && shouldDisplayAuthDialog}
      wrap={(children) => (
        <AuthGuard
          onClick={() => {
            clickOnProduct();
            setShouldDisplayAuthDialog(false);
            setRedirectUrl({ url: product.path, mode: 'push' });
          }}
          shouldPreventDefault={true}
          onAuth={() => (window.location.pathname = product.path)}
        >
          {children}
        </AuthGuard>
      )}
    >
      <ProductCard
        customClass={styles.catalog__productCardContainer}
        showProductTags={!product.isNewProduct}
        showSellerBadge
        showProductSize
        showProductExpressDelivery
        showProductLocation
        showProductDirectShipping
        showTaxesApply={isShowTaxIconEnabled}
        showDiscountPrice
        onClick={clickOnProduct}
        containerId={`product_id_${product.id}`}
        isNewProduct={product.isNewProduct}
        isGoodPriceVariantEnabled={isGoodPriceVariantEnabled}
        {...props}
        product={product}
        includeProductSchema={!schemaCatalogPage?.isSingleSchema}
        localCountriesApplied={Object.keys(filters || {}).includes('localCountries')}
      />
    </ConditionalWrapper>
  );
};

export default CatalogPageProductCard;
